'use client';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState, useCallback } from 'react';
import MWSVG from '../common/svg';
import BorderEdge from '../common/border-edge';
import axios from 'axios';
import NoRecord from '../common/no-record';
import { sanitizeTimestamps, createSafeDisplayDate } from '@/utils/timestamp-utils';

const Blog = () => {
    const [blogs, setBlogs] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [noRecords, setNoRecords] = useState(false);
    const limit: number = parseInt(process.env.NEXT_PUBLIC_API_PAGE_LIMIT || '3');
    const wordpressURL = process.env.NEXT_PUBLIC_WORDPRESS_URL || 'https://www.moweb.com/wp-json/wp/v2/posts';

    const fetchBlogs = useCallback(async () => {
        setFetching(true);

        // Skip WordPress API call in development mode or localhost
        if (process.env.NODE_ENV !== 'production' || 
            typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            console.log('Skipping WordPress API call in development/localhost mode');
            setNoRecords(true);
            setFetching(false);
            return;
        }

        try {
            // Validate URL is from trusted domain
            if (!wordpressURL || !wordpressURL.startsWith('https://')) {
                // Log error without exposing timing information
                console.warn('Invalid or insecure WordPress URL - using fallback');
                setNoRecords(true);
                setFetching(false);
                return;
            }

            // Validate URL is from moweb.com domain
            let urlObj;
            try {
                urlObj = new URL(wordpressURL);
            } catch {
                // Log error without exposing timing information
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Invalid URL format for WordPress API');
                }
                setNoRecords(true);
                setFetching(false);
                return;
            }
            
            if (!urlObj.hostname.endsWith('moweb.com')) {
                // Log error without exposing timing information
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('Untrusted domain for WordPress API - skipping blog fetch');
                }
                setNoRecords(true);
                setFetching(false);
                return;
            }

            const response = await axios.get(`${wordpressURL}?limit=${limit}`, {
                timeout: 10000, // 10 second timeout
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response && response.data && response.data.length > 0) {
                // Sanitize blog data and timestamps before setting state
                const rawBlogs = sanitizeTimestamps(response.data);
                const sanitizedBlogs = rawBlogs.map((blog: any) => ({
                    title: blog?.title || '',
                    content: blog?.content || '',
                    link: blog?.link || '',
                    image: blog?.image || '',
                    categories: Array.isArray(blog?.categories) ? blog.categories : [],
                    author: {
                        name: blog?.author?.name || 'Moweb Limited'
                    },
                    // Use safe date display without exposing precise timestamps
                    date: createSafeDisplayDate(blog?.date) || createSafeDisplayDate(blog?.published_at) || ''
                }));
                setBlogs(sanitizedBlogs);
            } else {
                setNoRecords(true);
            }
        } catch (error) {
            // Log error without exposing timing information in production
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching blogs:', error);
            }
            setNoRecords(true);
        }

        setFetching(false);
    }, [wordpressURL, limit]);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    return blogs.length > 0 ? (
        <>
            <section className="dark:pt-0 py-[80px] sm:py-[50px] md:py-[80px] dark:bg-black dark:text-light">
                <div className="container mx-auto">
                    <div className=" gap-4 flex justify-between pb-10 ">
                        <h2> Latest Articles</h2>
                        <div className="sm:hidden md:block">
                            <Link href="https://www.moweb.com/blog" className="btn btn-outline-primary  w-full text-center">
                                Explore All Blogs
                                <MWSVG id="tabler-icon-right-arrow" svgClassName="ic-24 ml-2.5" />
                            </Link>
                        </div>
                    </div>
                    {noRecords ? (
                        <NoRecord />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[40px] sm:gap-[30px] md:gap-[50px] ">
                            {fetching ? (
                                <></>
                            ) : (
                                blogs.map((blog: any, index: number) => {
                                    // Validate blog link is safe
                                    const isValidLink = blog?.link && 
                                        (blog.link.startsWith('https://www.moweb.com/') || 
                                         blog.link.startsWith('https://moweb.com/'));
                                    
                                    return (
                                    <div className="blog-col" key={index}>
                                        <Link href={isValidLink ? blog.link : '#'}>
                                            <div className=" bg-white dark:bg-black dark:text-light  rounded-lg overflow-hidden">
                                                <Link href={isValidLink ? blog.link : '#'} className="filter hover:brightness-75">
                                                    <Image
                                                        width={405}
                                                        height={192}
                                                        src={blog?.image ?? ''}
                                                        className="w-full h-full rounded-[10px] "
                                                        alt="Full-Stack vs MEAN vs MERN: A Guide to Picking the Right Technology Stack for 2024"
                                                        loading="lazy"
                                                    />
                                                </Link>
                                                <div className="py-4 flex flex-col gap-[10px]">
                                                    <div className="flex flex-wrap gap-[10px]">
                                                        <Link
                                                            href={isValidLink ? blog.link : '#'}
                                                            className="bg-gray-200 text-[#111] rounded-full px-3 py-1 text-sm font-semibold  "
                                                        >
                                                            {blog?.categories?.[0] ?? ''}
                                                        </Link>
                                                    </div>
                                                    <Link href={isValidLink ? blog.link : '#'}>
                                                        <h2 className=" text-2xl font-bold mb-0 line-clamp-2 text-[#111] dark:text-light">
                                                            {blog?.title ?? ''}
                                                        </h2>
                                                    </Link>
                                                    <p className="text-gray-600 line-clamp-2 text-base mb-0 dark:text-[#D7DEE5]  ">
                                                        {blog?.content ?? ''}
                                                    </p>
                                                    <div className="flex items-center gap-3">
                                                        <Image
                                                            src="/img/blog-contect.webp"
                                                            alt="Moweb Limited"
                                                            width={40}
                                                            height={40}
                                                            className=" rounded-full"
                                                            loading="lazy"
                                                        />
                                                        <div className="text-sm">
                                                            <div className="font-semibold text-[17px] pb-1">
                                                                By {blog?.author?.name ?? ''}
                                                            </div>
                                                            <div className="text-gray-500 dark:text-[#D7DEE5] text-[17px]">{blog?.date ?? ''}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                    <div className="sm:block md:hidden mt-10">
                        <Link href="https://www.moweb.com/blog" className="btn btn-outline-primary  w-full text-center">
                            Explore All Blogs
                            <MWSVG id="tabler-icon-right-arrow" svgClassName="ic-24 ml-2.5" />
                        </Link>
                    </div>
                </div>
            </section>
            <BorderEdge color="text-white" bg="bg-blue" />
        </>
    ) : (
        <></>
    );
};

export default Blog;
